// Human-readable labels for system identifiers that leak into the UI.
//
// The DB stores capabilities as `<category>.<action>` (e.g. `matter.read`),
// roles as snake_case (e.g. `super_admin`) and statuses as bare lowercase
// tokens (`active`, `in_progress`). Showing these verbatim looks like a leak
// of internal schema. These helpers convert them to title-cased English.
//
// Override maps win when a key needs a hand-tuned label (e.g. `pmt` -> `PMT`,
// `popia` -> `POPIA`); everything else falls back to a generic title-case.

const ACRONYMS = new Set(['ai', 'api', 'pdf', 'pmt', 'pkg', 'sla', 'sql', 'sms', 'ocr', 'eft', 'vat', 'xml', 'xero', 'popia', 'cpd', 'ffc', 'sca', 'lpc', 'gnr', 'sc', 'kc', 'qc']);

function titleCaseWord(w: string): string {
  if (!w) return w;
  if (ACRONYMS.has(w.toLowerCase())) return w.toUpperCase();
  return w[0].toUpperCase() + w.slice(1);
}

function titleCase(s: string): string {
  return s
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map(titleCaseWord)
    .join(' ');
}

// ---- Roles ----

const ROLE_OVERRIDES: Record<string, string> = {
  owner:        'Owner',
  super_admin:  'Super admin',
  admin:        'Admin',
  advocate:     'Advocate',
  pupil:        'Pupil',
  clerk:        'Clerk',
  accountant:   'Accountant',
  viewer:       'Viewer',
};

export function humaniseRole(role: string | null | undefined): string {
  if (!role) return '—';
  return ROLE_OVERRIDES[role] || titleCase(role);
}

// ---- Capability categories ----

const CATEGORY_OVERRIDES: Record<string, string> = {
  tenant:        'Chambers',
  members:       'Members',
  permissions:   'Permissions',
  parties:       'Parties',
  attorneys:     'Attorneys',
  matter:        'Matters',
  tasks:         'Tasks',
  time:          'Time entries',
  diary:         'Diary',
  documents:     'Documents',
  billing:       'Billing & rates',
  invoices:      'Invoices',
  payments:      'Payments',
  fee_book:      'Fee book',
  precedents:    'Precedents',
  trust:         'Trust accounting',
  signing:       'Signing',
  ai:            'AI assistant',
  integrations:  'Integrations',
  platform:      'Platform admin',
  popia:         'POPIA',
};

export function humaniseCategory(cat: string | null | undefined): string {
  if (!cat) return '';
  return CATEGORY_OVERRIDES[cat] || titleCase(cat);
}

// ---- Capability keys ----

const ACTION_OVERRIDES: Record<string, string> = {
  list:    'List',
  read:    'Read',
  create:  'Create',
  write:   'Edit',
  comment: 'Comment',
  manage:  'Manage',
  invite:  'Invite',
  admin:   'Admin',
  view:    'View',
  send:    'Send',
};

export function humaniseCapability(key: string | null | undefined): string {
  if (!key) return '';
  const [category, ...rest] = key.split('.');
  const action = rest.join('.');
  const cat = humaniseCategory(category);
  const act = ACTION_OVERRIDES[action] || titleCase(action || '');
  return action ? `${cat} · ${act}` : cat;
}

// ---- Status labels ----

const STATUS_OVERRIDES: Record<string, string> = {
  active:        'Active',
  inactive:      'Inactive',
  suspended:     'Suspended',
  archived:      'Archived',
  cancelled:     'Cancelled',
  trial:         'On trial',
  open:          'Open',
  in_progress:   'In progress',
  completed:     'Completed',
  on_hold:       'On hold',
  draft:         'Draft',
  in_review:     'In review',
  final:         'Final',
  filed:         'Filed',
  withdrawn:     'Withdrawn',
  invited:       'Invited',
  accepted:      'Accepted',
  revoked:       'Revoked',
  expired:       'Expired',
  paid:          'Paid',
  unpaid:        'Unpaid',
  partial:       'Partially paid',
  unbilled:      'Unbilled',
  on_proforma:   'On pro-forma',
  billed:        'Billed',
  excluded:      'Excluded',
};

export function humaniseStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return STATUS_OVERRIDES[status] || titleCase(status);
}

// ---- Generic fallback ----

/** Title-case any snake_case / kebab-case / dot-separated identifier. */
export function humanise(s: string | null | undefined): string {
  if (!s) return '';
  return titleCase(s);
}
